/**
 * Bare form layout primitive — vertical field rhythm + submit wiring. Always
 * prevents the native full-page submit (this is an SPA); the caller reads its
 * own field signals and receives a plain `onSubmit` callback. No validation,
 * no field state — that lives in the caller alongside `TextInput`/`Select`/
 * `Checkbox`/`Radio`.
 */
import type { JSX } from 'solid-js';
import { splitProps } from 'solid-js';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

const STYLE_ID = 'f4-form';
const CSS = `
.f4-form { display: flex; flex-direction: column; gap: var(--space-4); }
`;

export interface FormProps extends Omit<JSX.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  /** Emitted after the native submit is prevented. */
  readonly onSubmit: () => void;
  readonly class?: string;
  readonly children?: JSX.Element;
}

export function Form(props: FormProps): JSX.Element {
  ensureStyles(STYLE_ID, CSS);
  const [local, rest] = splitProps(props, ['onSubmit', 'class', 'children']);

  return (
    <form
      {...rest}
      class={cx('f4-form', local.class)}
      onSubmit={(event) => {
        event.preventDefault();
        local.onSubmit();
      }}
    >
      {local.children}
    </form>
  );
}
